import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { loading } from "../components/CustomComponent.js";
import Navbar from "../components/Navbar.js";
import Footer from "../components/Footer.js";
import { Btn, Reveal, Photo, FaqItem } from "../components/ui.js";
import { findPost, POSTS } from "../data/blog.js";

/* ============================================================
   LEVELUP ONE — Blog Detail (/blog/:slug)
   ============================================================ */

function Block({ b, i }) {
  switch (b.type) {
    case "h": return <h2 key={i}>{b.text}</h2>;
    case "p": return <p key={i}>{b.text}</p>;
    case "img": return (
      <figure key={i}>
        <Photo src={b.src} alt={b.caption || ""} fallback="🖼️" className="nx-detail-photo" />
        {b.caption && <figcaption>{b.caption}</figcaption>}
      </figure>
    );
    case "ul": return <ul key={i}>{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
    case "ol": return <ol key={i}>{b.items.map((it, j) => <li key={j}>{it}</li>)}</ol>;
    case "quote": return <blockquote key={i}>{b.text}</blockquote>;
    case "faq": return (
      <div key={i} style={{ margin: "28px 0" }}>
        <h2>คำถามที่เจ้าของร้านถามบ่อย</h2>
        {b.items.map((f, j) => <FaqItem key={j} q={f.q} a={f.a} defaultOpen={j === 0} />)}
      </div>
    );
    default: return null;
  }
}

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = findPost(slug);

  useEffect(() => {
    loading(false);
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="nx">
        <Navbar />
        <div className="container nx-404">
          <div>
            <div className="nx-404__code nx-gradient-text">ไม่พบบทความ</div>
            <p className="nx-muted mb-4">ไม่พบบทความที่คุณกำลังค้นหา</p>
            <Btn onClick={() => navigate("/blog")}>กลับไปหน้าบทความ</Btn>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const related = POSTS.filter((p) => p.slug !== post.slug && p.tag === post.tag)
    .concat(POSTS.filter((p) => p.slug !== post.slug && p.tag !== post.tag))
    .slice(0, 3);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="nx">
      <Navbar />

      {/* ===== HEADER ===== */}
      <section className="nx-section" style={{ paddingTop: 120, paddingBottom: 24 }}>
        <div className="container">
          <div className="nx-article">
            <Reveal>
              <div className="nx-breadcrumb">
                <Link to="/">หน้าหลัก</Link> &nbsp;/&nbsp;
                <Link to="/blog"> บทความ</Link> &nbsp;/&nbsp;
                <span> {post.tag}</span>
              </div>
              <span className="nx-post__tag mt-3">{post.tag}</span>
              <h1 className="nx-ink mt-2" style={{ fontSize: "clamp(1.9rem, 4.5vw, 3rem)" }}>{post.title}</h1>
              <p className="nx-subtitle mt-3">{post.excerpt}</p>

              <div className="nx-art-meta mt-4 justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <span className="nx-art-avatar" aria-hidden="true">{post.author.avatar}</span>
                  <div>
                    <div className="nx-ink fw-bold">{post.author.name}</div>
                    <div style={{ fontSize: "0.84rem" }}>{post.author.role}</div>
                  </div>
                </div>
                <div className="d-flex gap-3">
                  <span>🗓 {post.date}</span>
                  <span>⏱ {post.read}</span>
                  <span>👁 {post.views}</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* cover */}
      <section style={{ paddingBottom: 8 }}>
        <div className="container">
          <Reveal delay={80}>
            <div className="nx-article">
              <Photo src={post.cover} alt={post.title} fallback="📰" className="nx-detail-photo" style={{ borderRadius: 24 }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BODY ===== */}
      <section className="nx-section" style={{ paddingTop: 32 }}>
        <div className="container">
          <article className="nx-article">
            {post.body.map((b, i) => <Block key={i} b={b} i={i} />)}

            {/* share */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-5 pt-4" style={{ borderTop: "1px solid var(--nx-line)" }}>
              <span className="nx-muted" style={{ fontSize: "0.9rem" }}>แชร์บทความนี้</span>
              <div className="nx-share">
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" title="Facebook">f</a>
                <a href={`https://line.me/R/share?text=${encodeURIComponent(post.title + " " + shareUrl)}`} target="_blank" rel="noreferrer" title="LINE">L</a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noreferrer" title="X">𝕏</a>
              </div>
            </div>

            {/* author card */}
            <div className="nx-author-card mt-4">
              <span className="nx-art-avatar" style={{ width: 56, height: 56, fontSize: "1.6rem" }} aria-hidden="true">{post.author.avatar}</span>
              <div>
                <div className="nx-ink fw-bold">{post.author.name}</div>
                <div className="nx-muted" style={{ fontSize: "0.9rem" }}>{post.author.role} · ทีมงาน LevelUp One ที่ลงมือทำจริงให้ลูกค้าทุกวัน</div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* ===== RELATED ===== */}
      <section className="nx-section nx-bg-surface">
        <div className="container">
          <Reveal>
            <h2 className="nx-heading mb-4" style={{ fontSize: "1.6rem" }}>อ่านต่อ</h2>
          </Reveal>
          <div className="row g-4">
            {related.map((p, i) => (
              <div className="col-12 col-md-4" key={p.slug}>
                <Reveal delay={i * 60}>
                  <Link to={`/blog/${p.slug}`} className="nx-post d-block">
                    <Photo src={p.cover} alt={p.title} fallback="📝" />
                    <div className="nx-post__body">
                      <span className="nx-post__tag">{p.tag}</span>
                      <h3 className="nx-post__title">{p.title}</h3>
                      <div className="nx-post__meta"><span>🗓 {p.date}</span><span>⏱ {p.read}</span></div>
                    </div>
                  </Link>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TAGLINE ===== */}
      <section className="nx-section--tight nx-bg-soft">
        <div className="container">
          <Reveal>
            <div className="nx-tagline">
              <h2 className="nx-tagline__title">สร้างจากไทย — <span className="nx-gradient-text">ส่งมอบทั่วโลก</span></h2>
              <div className="nx-tagline__card">
                <span className="nx-eyebrow">เริ่มโปรเจกต์กับเรา</span>
                <p className="nx-card__desc my-3">คุยฟรี 30 นาที ไม่มีข้อผูกมัด แล้วเราจะช่วยประเมินแนวทางให้</p>
                <Btn to="/booking" block>พูดคุยกับเรา</Btn>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
